import { Translate } from 'next-translate';
import { MappedTransportType, PostcodeLimitationMode } from 'types/transport';
import * as Yup from 'yup';
import { AnyObject, Maybe } from 'yup/lib/types';

declare module 'yup' {
    interface StringSchema<
        TType extends Maybe<string> = string | undefined,
        TContext extends AnyObject = AnyObject,
        TOut extends TType = TType,
    > extends Yup.BaseSchema<TType, TContext, TOut> {
        validatePostCode(
            locale: string,
            t: Translate,
            transport: MappedTransportType | null,
        ): StringSchema<TType, TContext>;
        validateCompanyNumber(locale: string, t: Translate): StringSchema<TType, TContext>;
        validateCompanyTaxNumber(locale: string, t: Translate): StringSchema<TType, TContext>;
        validateCompanyTaxIdNumber(locale: string, t: Translate): StringSchema<TType, TContext>;
    }

    interface DateSchema {
        validateCustomerBirthDay(t: Translate, maximalDate: Date, minimalAge: number): DateSchema;
    }
}

Yup.addMethod(Yup.string, 'validatePostCode', (locale, t, transport) => {
    const rule = Yup.string()
        .matches(/^[0-9 -]*$/, t('Please enter only numbers, - or space'))
        .test('limitation', t('Postcode is not supported with selected shipping method'), (value) => {
            if (
                transport === null ||
                transport?.postcodeLimitationMode === null ||
                transport?.postcodeLimitation === null ||
                value === undefined
            ) {
                return true;
            }
            const limitationRules: string[] = transport?.postcodeLimitation
                .split(',')
                .map((item: string) => item.trimStart());
            const postcodeStartsWithAtLeastOneRule = limitationRules.some((rule) => value.startsWith(rule));

            return (
                (postcodeStartsWithAtLeastOneRule &&
                    transport?.postcodeLimitationMode === PostcodeLimitationMode.ALLOW) ||
                (!postcodeStartsWithAtLeastOneRule && transport?.postcodeLimitationMode === PostcodeLimitationMode.DENY)
            );
        });
    switch (locale) {
        case 'pl':
            return rule.test(
                'len',
                t('This value must be exactly {{ count }} characters', { count: 5 }),
                (value) => value !== undefined && value.replace(' ', '').replace('-', '').length === 5,
            );
        case 'hu':
            return rule.test(
                'len',
                t('This value must be exactly {{ count }} characters', { count: 4 }),
                (value) => value !== undefined && value.replace(' ', '').replace('-', '').length === 4,
            );
        case 'pt':
            return Yup.string().test(
                'len',
                t('This value must be exactly {{ count }} characters', { count: 7 }),
                (value) => value !== undefined && value.replace(' ', '').replace('-', '').length === 7,
            );
        case 'de-AT':
            return Yup.string().test(
                'len',
                t('This value must be exactly {{ count }} characters', { count: 4 }),
                (value) => value !== undefined && value.replace(' ', '').replace('-', '').length === 4,
            );
        default:
            return rule.test(
                'len',
                t('This value must be exactly {{ count }} characters', { count: 5 }),
                (value) => value !== undefined && value.replace(' ', '').replace('-', '').length === 5,
            );
    }
});

Yup.addMethod(Yup.string, 'validateCompanyNumber', (locale, t) => {
    switch (locale) {
        case 'pl':
            return Yup.string()
                .matches(/^[0-9]*$/, t('Please enter only numbers'))
                .test(
                    'len',
                    t('This value must be exactly {{ count }} characters', { count: 9 }),
                    (value) => value?.length === 0 || value?.length === 9,
                );
        case 'hu':
            return Yup.string();
        case 'pt':
            return Yup.string().matches(/^[0-9]*$/, t('Please enter only numbers'));
        case 'de':
            return Yup.string()
                .matches(/^[0-9]*$/, t('Please enter only numbers'))
                .test(
                    'len',
                    t('This value must be exactly {{ count }} characters', { count: 11 }),
                    (value) => value?.length === 0 || value?.length === 11,
                );
        case 'es':
            return Yup.string().matches(/^([A-Z]{1}\d{8}|\d{8}[A-Z]{1})$/, {
                message: t('This value must contain 8 digits and 1 letter'),
                excludeEmptyString: true,
            });
        case 'it-IT':
            return Yup.string()
                .required(t('Please enter identification number'))
                .matches(/^[0-9]*$/, t('Please enter only numbers'))
                .test(
                    'len',
                    t('This value must be exactly {{ count }} characters', { count: 11 }),
                    (value) => value?.length === 11,
                );
        default:
            return Yup.string()
                .required(t('Please enter identification number'))
                .matches(/^[0-9]*$/, t('Please enter only numbers'))
                .test(
                    'len',
                    t('This value must be exactly {{ count }} characters', { count: 8 }),
                    (value) => value?.length === 8,
                );
    }
});

Yup.addMethod(Yup.string, 'validateCompanyTaxNumber', (locale, t) => {
    switch (locale) {
        case 'pl':
            return Yup.string()
                .required(t('Please enter tax identification number'))
                .matches(/^[0-9]*$/, t('Please enter only numbers'))
                .test(
                    'len',
                    t('This value must be exactly {{ count }} characters', { count: 10 }),
                    (value) => value?.length === 10,
                );
        case 'cs':
            return Yup.string()
                .matches(/^[A-Za-z0-9]*$/, {
                    message: t('Please enter only letters and numbers'),
                    excludeEmptyString: true,
                })
                .test(
                    'len',
                    t('This value cannot be shorter than {{ count }} characters', { count: 10 }),
                    (value) => value?.length === 0 || (value?.length !== undefined && value.length >= 10),
                )
                .test(
                    'len',
                    t('This value cannot be longer than {{ count }} characters', { count: 12 }),
                    (value) => value?.length !== undefined && value.length <= 12,
                );
        case 'sk':
            return Yup.string()
                .matches(/^[0-9]*$/, t('Please enter only numbers'))
                .test(
                    'len',
                    t('This value must be exactly {{ count }} characters', { count: 10 }),
                    (value) => value?.length === 0 || value?.length === 10,
                );
        case 'hu':
            return Yup.string()
                .required(t('Please enter tax identification number'))
                .matches(/^[-\d]+$/, t('Please enter tax identification number'))
                .test(
                    'len',
                    t('This value cannot be shorter than {{ count }} characters', { count: 11 }),
                    (value) => value?.length === 0 || (value?.length !== undefined && value.length >= 11),
                )
                .test(
                    'len',
                    t('This value cannot be longer than {{ count }} characters', { count: 13 }),
                    (value) => value?.length !== undefined && value.length <= 13,
                );
        case 'pt':
            return Yup.string()
                .required(t('Please enter tax identification number'))
                .matches(/^[0-9]*$/, t('Please enter only numbers'))
                .test(
                    'len',
                    t('This value must be exactly {{ count }} characters', { count: 9 }),
                    (value) => value?.length === 0 || value?.length === 9,
                );
        case 'de':
            return Yup.string()
                .matches(/^[A-Za-z0-9]*$/, {
                    message: t('Please enter only letters and numbers'),
                    excludeEmptyString: true,
                })
                .test(
                    'len',
                    t('This value cannot be longer than {{ count }} characters', { count: 11 }),
                    (value) => value?.length !== undefined && value.length <= 11,
                );
        case 'it-IT':
            return Yup.string()
                .trim()
                .required(t('Please enter tax identification number'))
                .matches(/^(?:[A-Za-z0-9]{16}|\d{11})$/, t('Please check Codice Fiscale format'));
        default:
            return Yup.string()
                .matches(/^[0-9A-Z]*([0-9]+[A-Z]+|[A-Z]+[0-9]+)[0-9A-Z]*$/, {
                    message: t('Please enter tax identification number'),
                    excludeEmptyString: true,
                })
                .test(
                    'len',
                    t('This value must be exactly {{ count }} characters', { count: 10 }),
                    (value) => value?.length === 0 || value?.length === 10,
                );
    }
});

Yup.addMethod(Yup.string, 'validateCompanyTaxIdNumber', (locale, t) => {
    switch (locale) {
        case 'sk':
            return Yup.string()
                .matches(/^[A-Za-z]{2}\d{10}$/, {
                    message: t('Please enter exactly 2 letters and 10 numbers'),
                    excludeEmptyString: true,
                })
                .test(
                    'len',
                    t('This value must be exactly {{ count }} characters', { count: 12 }),
                    (value) => value?.length === 0 || value?.length === 12,
                );
        case 'it-IT':
            return Yup.string()
                .trim()
                .required(t('Please enter PEC or SDI'))
                .test(
                    'pec-or-sdi',
                    t('Please enter a valid PEC email address or SDI code'),
                    (value) =>
                        value !== undefined &&
                        (Yup.string().email().isValidSync(value) || /^[A-Za-z0-9]{7}$/.test(value)),
                );
        default:
            return Yup.string();
    }
});

Yup.addMethod(Yup.date, 'validateCustomerBirthDay', (t: Translate, maximalDate: Date, minimalAge: number) => {
    return Yup.date()
        .nullable()
        .max(maximalDate, t('The minimum age for registration is {{ age }}', { age: minimalAge }));
});

export default Yup;
